import { Controller } from "@hotwired/stimulus";

export default class AncillaryPickerController extends Controller {
  static targets = [
    "checkbox",
    "ancillaryTotal",
    "grandTotal",
    "clientPrice",
    "mirrorToggle",
    "freeText",
  ];

  static values = { agencyCents: Number, currency: String };

  connect() {
    this.update();
    this.refreshAllHints();
  }

  // Fired on any selection change (single toggle or a checkbox inside a modal).
  change(event) {
    if (event?.target && this.mirroring()) {
      this.counterparts(this.checkboxTargets, event.target).forEach((cb) => {
        // eslint-disable-next-line no-param-reassign
        cb.checked = event.target.checked;
      });
    }
    this.update();
    this.refreshAllHints();
  }

  // ── Mirroring across legs (U5) ───────────────────────────────────────────
  // With the toggle on, a selection (or its free text) on one leg is copied to
  // the same service (RFISC code) for the same traveller on every other leg that
  // offers it. Each leg keeps its own form entry, price and segments.

  mirroring() {
    return this.hasMirrorToggleTarget && this.mirrorToggleTarget.checked;
  }

  mirrorFreeText(event) {
    if (!this.mirroring()) return;

    this.counterparts(this.freeTextTargets, event.target).forEach((input) => {
      // eslint-disable-next-line no-param-reassign
      input.value = event.target.value;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  counterparts(elements, source) {
    const { ancillaryCode, legIndex, participantId } = source.dataset;
    if (!ancillaryCode) return [];

    return elements.filter(
      (el) =>
        el !== source &&
        el.dataset.ancillaryCode === ancillaryCode &&
        el.dataset.participantId === participantId &&
        el.dataset.legIndex !== legIndex
    );
  }

  update() {
    const selectedCents = this.checkboxTargets
      .filter((cb) => cb.checked)
      .reduce((sum, cb) => sum + parseInt(cb.dataset.priceCents || "0", 10), 0);

    const totalCents = this.agencyCentsValue + selectedCents;

    this.ancillaryTotalTargets.forEach((el) => {
      // eslint-disable-next-line no-param-reassign
      el.textContent = this.formatCurrency(selectedCents);
      el.closest("[data-ancillary-row]")?.classList.toggle(
        "hidden",
        selectedCents === 0
      );
    });

    this.grandTotalTargets.forEach((el) => {
      // eslint-disable-next-line no-param-reassign
      el.textContent = this.formatCurrency(totalCents);
    });

    if (this.hasClientPriceTarget) {
      this.clientPriceTarget.value = (totalCents / 100).toFixed(2);
    }
  }

  // ── Assignment hints ─────────────────────────────────────────────────────

  refreshAllHints() {
    this.element
      .querySelectorAll("[data-service-row]")
      .forEach((row) => this.refreshHint(row));
  }

  // eslint-disable-next-line class-methods-use-this
  refreshHint(row) {
    const boxes = Array.from(
      row.querySelectorAll("[data-ancillary-picker-target='checkbox']")
    );
    const checked = boxes.filter((b) => b.checked);
    const names = checked.map((b) => b.dataset.participantName).filter(Boolean);

    const countEl = row.querySelector("[data-hint-count]");
    const namesEl = row.querySelector("[data-hint-names]");
    const pill = row.querySelector("[data-hint-pill]");

    if (countEl) countEl.textContent = `${checked.length}/${boxes.length}`;
    if (namesEl) {
      namesEl.textContent = names.join(", ");
      namesEl.title = names.join(", ");
    }
    if (pill) {
      pill.title = names.join(", ");
      pill.classList.toggle("text-tourrise-blue", checked.length > 0);
      pill.classList.toggle("font-semibold", checked.length > 0);
      pill.classList.toggle("text-gray-500", checked.length === 0);
    }
  }

  // ── Modal ────────────────────────────────────────────────────────────────

  openModal(event) {
    this.closeModals();
    const key = event.currentTarget.dataset.modalKey;
    this.element
      .querySelector(`[data-modal="${key}"]`)
      ?.classList.remove("hidden");
  }

  // eslint-disable-next-line class-methods-use-this
  closeModal(event) {
    event.currentTarget.closest("[data-modal]")?.classList.add("hidden");
  }

  closeModals() {
    this.element
      .querySelectorAll("[data-modal]")
      .forEach((m) => m.classList.add("hidden"));
  }

  formatCurrency(cents) {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: this.currencyValue || "EUR",
    }).format(cents / 100);
  }
}
